import { BaseAnimation } from './BaseAnimation';
import { Compass } from '../objects/Compass';
import { BaseObject } from '../objects/BaseObject';
import { Point2D } from '../types';
import { AnimationEngine } from '../core/AnimationEngine';

export class MoveCompass extends BaseAnimation {
  private startPos: Point2D;
  private targetPos: Point2D;
  private startRadius: number;
  private targetRadius: number;

  constructor(
    compass: Compass,
    targetPos: Point2D,
    targetRadius: number,
    easing = AnimationEngine.Easings.easeInOutCubic
  ) {
    super(compass, easing);
    this.startPos = { ...compass.needlePos };
    this.targetPos = { ...targetPos };
    this.startRadius = compass.radius;
    this.targetRadius = targetRadius;
  }

  protected update(progress: number): void {
    const compass = this.target as Compass;
    compass.setVisible(true);

    if (progress === 0) {
      this.startPos = { ...compass.needlePos };
      this.startRadius = compass.radius;
    }

    const curX = this.startPos.x + (this.targetPos.x - this.startPos.x) * progress;
    const curY = this.startPos.y + (this.targetPos.y - this.startPos.y) * progress;
    const curR = this.startRadius + (this.targetRadius - this.startRadius) * progress;

    compass.setPosition({ x: curX, y: curY }, curR, compass.rotationRad);
  }
}

export class RotateCompass extends BaseAnimation {
  private startAngle: number;
  private targetAngle: number;

  constructor(
    compass: Compass,
    targetAngleRad: number,
    easing = AnimationEngine.Easings.easeInOutCubic
  ) {
    super(compass, easing);
    this.startAngle = compass.rotationRad;
    this.targetAngle = targetAngleRad;
  }

  protected update(progress: number): void {
    const compass = this.target as Compass;
    compass.setVisible(true);

    if (progress === 0) {
      this.startAngle = compass.rotationRad;
    }

    const curAngle = this.startAngle + (this.targetAngle - this.startAngle) * progress;
    compass.setPosition(compass.needlePos, compass.radius, curAngle);
  }
}

export class Fade extends BaseAnimation {
  private fadeIn: boolean;

  constructor(target: BaseObject, fadeIn: boolean = true, easing = AnimationEngine.Easings.easeInOutQuad) {
    super(target, easing);
    this.fadeIn = fadeIn;
  }

  protected update(progress: number): void {
    const obj = this.target as BaseObject;
    const opacity = this.fadeIn ? progress : 1 - progress;
    obj.setVisible(opacity > 0);
    obj.setOpacity(opacity);
  }
}

export class Highlight extends BaseAnimation {
  constructor(target: BaseObject, easing = AnimationEngine.Easings.easeInOutSine) {
    super(target, easing);
  }

  protected update(progress: number): void {
    const obj = this.target as BaseObject;
    obj.setVisible(true);
    
    // Create pulse scale / glow effect
    const pulse = 1.0 + Math.sin(progress * Math.PI) * 0.35;

    if (obj.element) {
      obj.element.setAttribute('filter', progress > 0 && progress < 1 ? 'url(#glow-filter)' : '');
      obj.element.setAttribute('transform', `scale(${pulse})`);
      obj.element.style.transformOrigin = 'center';
    }
  }
}
